import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Packages', path: '/#packages' },
    { name: 'About', path: '/about-us' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
    { name: 'Weddings', path: 'https://destinationweddingsandevents.in/', isExternal: true, isNew: true }
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavClick = (e, path) => {
    if (path.startsWith('/#')) {
      e.preventDefault();
      const elementId = path.substring(2);
      if (location.pathname !== '/') {
        window.location.href = path;
      } else {
        const element = document.getElementById(elementId);
        element?.scrollIntoView({ behavior: 'smooth' });
        setIsOpen(false);
      }
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled
        ? 'bg-white shadow-2xl py-2' 
        : 'bg-white/95 shadow-lg py-3'
    }`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_cdbf81a7-7ac8-4363-9dd6-522b29aa8022/artifacts/lst2w5gp_WhatsApp%20Image%202026-01-24%20at%2017.19.09.jpeg"
              alt="Dharamshala Dalhousie Tour"
              className={`transition-all duration-300 ${isScrolled ? 'h-14 md:h-16' : 'h-16 md:h-20'}`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              link.isExternal ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative text-sm font-semibold transition-all py-2 text-pink-600 hover:text-pink-700 flex items-center gap-1"
                >
                  {link.name}
                  {link.isNew && (
                    <span className="absolute -top-1 -right-6 px-1.5 py-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[10px] font-bold rounded-full animate-pulse">
                      NEW
                    </span>
                  )}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className={`text-sm font-semibold transition-all relative py-2 ${
                    isActive(link.path)
                      ? 'text-orange-600'
                      : 'text-gray-700 hover:text-orange-600'
                  }`}
                >
                  {link.name}
                  {isActive(link.path) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500 rounded-full"></span>
                  )}
                </Link>
              )
            ))}
          </div>

          {/* CTA Buttons - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <a href="https://wa.me/919816154248" target="_blank" rel="noopener noreferrer">
              <Button className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-5 py-2.5 rounded-full font-semibold shadow-lg shadow-green-500/30 transition-all hover:scale-105">
                <MessageCircle className="h-4 w-4 mr-2" />
                WhatsApp
              </Button>
            </a>
            <a href="tel:9816154248">
              <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-5 py-2.5 rounded-full font-semibold shadow-lg shadow-orange-500/30 transition-all hover:scale-105">
                <Phone className="h-4 w-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100 mt-4">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                link.isExternal ? (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                    className="px-4 py-3 rounded-xl text-base font-semibold transition-colors text-pink-600 bg-pink-50 flex items-center justify-between"
                  >
                    <span>{link.name}</span>
                    {link.isNew && (
                      <span className="px-2 py-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold rounded-full">
                        NEW
                      </span>
                    )}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={(e) => {
                      handleNavClick(e, link.path);
                      setIsOpen(false);
                    }}
                    className={`px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                      isActive(link.path) 
                        ? 'text-orange-600 bg-orange-50' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <div className="flex gap-2 mt-4 px-4">
                <a href="https://wa.me/919816154248" target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full font-semibold">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    WhatsApp
                  </Button>
                </a>
                <a href="tel:9816154248" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full font-semibold">
                    <Phone className="h-4 w-4 mr-2" />
                    Call
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
